import React from 'react';
import { Link } from 'react-router-dom';
import { buildersIn, modelsFrom, platformsFrom } from '../../utils/ecosystem';
import { formatReleased } from '../../utils/format';
import { SourceRef } from '../SourceRef';
interface CountryEcosystemProps {
  cca3: string;
  countryName: string;
}
export function CountryEcosystem({
  cca3,
  countryName
}: CountryEcosystemProps) {
  const orgs = buildersIn(cca3);
  const models = modelsFrom(cca3);
  const open = models.filter(m => m.license !== 'proprietary');
  const api = models.filter(m => m.license === 'proprietary');
  const platforms = platformsFrom(cca3);
  return <section aria-labelledby="eco-title" className="mt-12" data-id="element-231" data-mp="2wj1gh8qko5tt">
      <h2 id="eco-title" className="font-serif text-2xl text-ink" data-id="element-232" data-mp="30j1xn1522v4k">
        The AI ecosystem in {countryName}
      </h2>
      <p className="mt-1 text-[13px] text-muted" data-id="element-233" data-mp="124gwyzdzmgc0">Organizations headquartered here, the models their labs released, and the platforms that serve them.</p>

      <div className="mt-6" data-id="element-234" data-mp="1673vfj55yzi6">
        <div className="flex items-baseline justify-between gap-3" data-id="element-235" data-mp="5qq0akdj23ro">
          <h3 className="text-[15px] font-medium text-ink" data-id="element-236" data-mp="5s14glavxdjb">
            Builders <span className="font-mono text-muted" data-id="element-237" data-mp="xwwpztsp1h6">{orgs.length}</span>
          </h3>
          <Link to="/builders" className="text-[13px] text-muted hover:text-machine" data-id="element-238" data-mp="3s6kfq94kcclk">
            All builders →
          </Link>
        </div>
        {orgs.length === 0 ? <p className="mt-2 text-[13px] text-faint" data-id="element-239" data-mp="oplp1g6m9py4">No organizations in our builders dataset are headquartered here yet.</p> : <ul className="mt-3 divide-y divide-line/60 border-y border-line" data-id="element-240" data-mp="jqipgb9eobxj">
            {orgs.map(b => <li key={b.org} className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 py-2.5 md:grid-cols-[14rem_minmax(0,1fr)_auto]" data-id="element-241" data-mp="esz8h3lbpl1w">
                <span className="text-[14px] text-ink" data-id="element-242" data-mp="2zgvgo1beymml">
                  {b.org}
                  <SourceRef href={b.sourceUrl} label={`${b.org} — official site`} grade="A" figure={b.org} data-id="element-243" data-mp="2ui0ldzw9f5ll" />
                </span>
                <span className="col-span-2 row-start-2 text-[13px] text-muted md:col-span-1 md:row-start-auto" data-id="element-244" data-mp="1mibpj85kporh">{b.builds}</span>
                <span className="row-start-1 text-right font-mono text-[11px] text-faint md:row-start-auto" data-id="element-245" data-mp="1qjn66o6gyznj">
                  {b.type} · {b.product}
                </span>
              </li>)}
          </ul>}
      </div>

      <div className="mt-10" data-id="element-246" data-mp="12um5jn3vosst">
        <h3 className="text-[15px] font-medium text-ink" data-id="element-247" data-mp="12uc9bhs8ppeo">
          Models from {countryName} <span className="font-mono text-muted" data-id="element-248" data-mp="17ludsfth284e">{models.length}</span>
        </h3>
        {models.length === 0 ? <p className="mt-2 text-[13px] text-faint" data-id="element-249" data-mp="tuz1etjrl8w2">No models in our curated table were released by labs headquartered here.</p> : <div className="mt-3 grid gap-8 md:grid-cols-2" data-id="element-250" data-mp="yqwjp5el8pbh">
            {[{
          title: 'Open-weight',
          list: open,
          tone: 'text-live'
        }, {
          title: 'API-only',
          list: api,
          tone: 'text-muted'
        }].map(g => <div key={g.title} data-id="element-251" data-mp="589fuy09e1hs">
                <p className={`font-mono text-[11px] ${g.tone}`} data-id="element-252" data-mp="59jfhrmrfhqj">
                  {g.title} · {g.list.length}
                </p>
                <ul className="mt-2 space-y-1.5" data-id="element-253" data-mp="19sg4zj63tae">
                  {g.list.length === 0 && <li className="text-[13px] text-faint" data-id="element-254" data-mp="f8iiouoral65">None listed</li>}
                  {g.list.map(m => <li key={m.model} className="flex items-baseline justify-between gap-3 text-[13.5px]" data-id="element-255" data-mp="aagy25bomdsk">
                      <span className="truncate text-ink" data-id="element-256" data-mp="k6a7hfu3tr64">
                        {m.model}
                        <SourceRef href={m.sourceUrl} label={`${m.model} announcement`} grade="A" data-id="element-257" data-mp="k4h9eoxk4f9q" />
                      </span>
                      <span className="shrink-0 font-mono text-[11px] text-faint" data-id="element-258" data-mp="sy3t3gohps2k">
                        {m.lab} · {formatReleased(m.released)}
                      </span>
                    </li>)}
                </ul>
              </div>)}
          </div>}
      </div>

      <div className="mt-10" data-id="element-259" data-mp="2wgae5l3qovii">
        <h3 className="text-[15px] font-medium text-ink" data-id="element-260" data-mp="2wez97hyk5hpt">
          Platforms from {countryName} <span className="font-mono text-muted" data-id="element-261" data-mp="31dainrkxxq7i">{platforms.length}</span>
        </h3>
        {platforms.length === 0 ? <p className="mt-2 text-[13px] text-faint" data-id="element-262" data-mp="3663lg9wumogy">No model, inference or compute platforms headquartered here in our dataset.</p> : <ul className="mt-3 flex flex-wrap gap-2" data-id="element-263" data-mp="2dh64ca4dixps">
            {platforms.map(p => <li key={p.name} className="rounded-md border border-line px-2.5 py-1.5 text-[13px] text-ink" data-id="element-264" data-mp="2ifhk11hco2fx">
                {p.name}
                <SourceRef href={p.url} label={`${p.name} — official page`} grade="A" data-id="element-265" data-mp="2ne156zo4da78" />
                <span className="ml-2 font-mono text-[10.5px] text-faint" data-id="element-266" data-mp="2seyj3023ji2x">{p.kind}</span>
              </li>)}
          </ul>}
      </div>
    </section>;
}